import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  HttpGetConfig,
  NgEntityService,
  NgEntityServiceConfig,
} from '@datorama/akita-ng-entity-service';
import { of } from 'rxjs';
import { finalize, take, tap } from 'rxjs/operators';
import { AuthQuery } from '../auth';
import { RequestService } from '../request';
import { UserService } from '../user';
import { Event } from './event.model';
import { EventQuery } from './event.query';
import { EventState, EventStore } from './event.store';
import { EventParams, EventStatus } from './types';

@NgEntityServiceConfig({
  resourceName: 'events',
})
@Injectable()
export class EventService extends NgEntityService<EventState> {
  constructor(
    protected store: EventStore,
    private query: EventQuery,
    private authQuery: AuthQuery,
    private userService: UserService,
    private requestService: RequestService
  ) {
    super(store);
  }

  setActive(id: string) {
    this.store.setActive(id);
    if (!this.query.getHasCache()) {
      super.get(id).subscribe();
    }
  }

  get(config: HttpGetConfig<Event> = { append: true }) {
    const store = this.store.getValue();
    const params = this._convertParams(store.params);
    return super.get<Event[]>({ ...config, params }).pipe(
      tap((events) => {
        if (config.append) {
          this.store.upsertMany(events || []);
        }
      })
    );
  }

  getUserEvents() {
    const { isLoggedIn } = this.authQuery.getValue();
    if (isLoggedIn) {
      return super
        .getHttp()
        .get<Event[]>(this.api + '/joined')
        .pipe(
          tap((userEvents) => {
            this.store.upsertMany(userEvents);
          })
        );
    }
    return of([]);
  }

  byId(id: string, force = false) {
    const { entities } = this.store.getValue();
    if (entities && entities[id] && !force) {
      return of(entities[id]);
    }
    const params: any = {};

    if (force) {
      params.d = Date.now();
    }
    return super
      .getHttp()
      .get<Event>(`${this.api}/${id}`, { params })
      .pipe(
        tap((event) => {
          this.store.upsert(id, (state) => {
            return {
              ...event,
              extended: false,
            };
          });
        })
      );
  }

  setParams(params: Partial<EventParams>, replace = false, refresh = true) {
    // Set params (maybe in BE ) and update the store
    this.store.update((state) => {
      if (replace) {
        return { ...state, params: { ...params } as any };
      } else {
        return { ...state, params: { ...state.params, ...params } };
      }
    });
    if (refresh) {
      const store = this.store.getValue();
      const params = this._convertParams(store.params);
      return super
        .getHttp()
        .get<Event[]>(`${this.api}`, { params })
        .pipe(
          tap((events) => {
            this.store.remove((ev: Event) => !ev.joined);
            this.store.upsertMany(events || []);
          }),
          finalize(() => {
            this.store.setLoading(false);
          })
        );
    }
    return of({} as any);
  }

  joinEvent(groupId: string) {
    // Make an API request to generate a join request to specific event
    return this.requestService.add({
      type: 'JoinGroup',
      requestedForDocumentId: groupId,
      requestedForDocumentName: 'group',
    } as any);
  }

  handleJoinEventRequest(
    { eventId, userId }: { eventId: string; userId: string },
    notify = false,
    cb = () => void 0,
    updater = async () => {
      await this.byId(eventId, true).toPromise();
    }
  ) {
    this.userService
      .getUserInfo(userId)
      .pipe(take(1))
      .subscribe(async (_) => {
        if (notify) {
        }
        // This will refresh
        updater();
      });
  }

  getUser() {
    return this.authQuery.getValue().user;
  }

  private _convertParams(params: EventParams) {
    const httpParams = new HttpParams({
      fromObject: {
        lat: params.location ? params.location.lat.toString() : '0',
        lng: params.location ? params.location.lng.toString() : '0',
        maxDistance: (params.maxDistance || 5).toString(),
        bookedOnly: `${params.bookedOnly || null}`,
        categories: (params.selectedCategories || []).join(','),
        sortBy: params.sortBy as string,
        date: params.date || new Date().toJSON(),
      },
    });
    return httpParams;
  }

  updateStatus(id: string, status: EventStatus) {
    return this.getHttp()
      .put<Event>(`${this.api}/${id}/status`, { status })
      .pipe(
        tap((event) => {
          this.store.update(id, event);
        })
      );
  }

}
